import { forwardRef, useState } from "react";
import { useRadio } from "@/contexts/RadioContext";
import { useCallActive } from "@/hooks/useCallActive";
import { useTranslation } from "react-i18next";
import { Play, Pause, X, Volume2, VolumeX, Speaker, Loader2, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const RadioMiniPlayer = forwardRef<HTMLDivElement, Record<string, never>>(function RadioMiniPlayer(_, ref) {
  const {
    currentStation,
    isPlaying,
    isMuted,
    volume,
    isBuffering,
    play,
    stop,
    toggleMute,
    setVolume,
    outputDevices,
    currentOutputId,
    selectOutput,
    refreshOutputDevices,
  } = useRadio();

  const [showVolume, setShowVolume] = useState(false);
  const inCall = useCallActive();
  const { t } = useTranslation("common");

  if (!currentStation) return null;

  return (
    <div
      ref={ref}
      aria-disabled={inCall}
      className={cn(
        "pointer-events-auto mx-auto flex max-w-md items-center gap-3 rounded-[1.25rem] border border-border bg-card/95 px-3 py-2.5 shadow-card backdrop-blur supports-[backdrop-filter]:bg-card/80 transition-opacity",
        inCall && "opacity-50 grayscale [&_button]:pointer-events-none [&_[role=slider]]:pointer-events-none"
      )}
    >
      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">

        {currentStation.favicon ? (
          <img
            src={currentStation.favicon}
            alt=""
            className="h-full w-full object-cover rounded-xl"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <Radio className="h-5 w-5 text-primary" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">
          {currentStation.name}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {inCall ? t("radioPausedDuringCall") : isBuffering ? "Connecting..." : currentStation.country || "Live Radio"}
        </p>
      </div>


      <Popover open={showVolume} onOpenChange={setShowVolume}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full shrink-0 touchable no-context-menu">
            {isMuted || volume === 0 ? (
              <VolumeX className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Volume2 className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-4" side="top" align="center">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Volume</span>
                <Button variant="ghost" size="sm" className="min-h-[44px] px-2 text-xs" onClick={toggleMute}>
                  {isMuted ? "Unmute" : "Mute"}
                </Button>
              </div>
              <Slider
                value={[isMuted ? 0 : volume * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={([v]) => {
                  if (isMuted) toggleMute();
                  setVolume(v / 100);
                }}
              />
            </div>

            {outputDevices.length > 1 && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center gap-1.5">
                  <Speaker className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Output Device</span>
                </div>
                <div className="space-y-1">
                  {outputDevices.map((device) => (
                    <button
                      key={device.deviceId}
                      onClick={() => selectOutput(device.deviceId)}
                      className={cn(
                        "min-h-[44px] w-full rounded-lg px-3 py-2 text-left text-xs transition-colors truncate touchable no-context-menu",
                        (currentOutputId === device.deviceId || (!currentOutputId && device.deviceId === "default"))
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {device.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {outputDevices.length <= 1 && (
              <div className="border-t pt-3">
                <button
                  onClick={refreshOutputDevices}
                  className="min-h-[44px] w-full py-1 text-center text-xs text-muted-foreground hover:text-foreground touchable no-context-menu"
                >
                  <Speaker className="h-3.5 w-3.5 inline mr-1" />
                  Detect speakers & Bluetooth
                </button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 rounded-full shrink-0 touchable no-context-menu"
        onClick={() => {
          if (isPlaying || isBuffering) {
            stop();
          } else {
            play(currentStation);
          }
        }}
      >
        {isBuffering ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4 text-foreground" />
        ) : (
          <Play className="h-4 w-4 ml-0.5 text-foreground" />
        )}
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 rounded-full shrink-0 touchable no-context-menu"
        onClick={stop}
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  );
});

RadioMiniPlayer.displayName = "RadioMiniPlayer";
